import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo, aboutInfo } from '@/mocks/clCommunityData';

export default function AboutPage() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于${siteInfo.name}`,
    description: siteInfo.description,
    url: 'https://clshequ.com/about',
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: 'https://clshequ.com/',
    description: siteInfo.description,
    foundingDate: '2018',
    email: aboutInfo.contact.email,
    telephone: aboutInfo.contact.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '中关村科技园创新大厦12层',
      addressLocality: '北京',
      addressRegion: '海淀区',
      addressCountry: 'CN',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      <Navbar />
      <main className="pt-16">
        <section className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <div className="mt-6 text-center">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-3">
                关于{siteInfo.name}
              </h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                {aboutInfo.brandIntro}
              </p>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="mb-14">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
              品牌故事
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              {aboutInfo.brandStory}
            </p>
          </div>

          <div className="mb-14">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
              核心价值
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
              {aboutInfo.values.map((v, i) => (
                <div
                  key={i}
                  className="bg-background-50 rounded-xl border border-background-200/70 p-5 md:p-6"
                >
                  <div className="w-10 h-10 rounded-lg bg-accent-500 flex items-center justify-center mb-3">
                    <i className="ri-check-double-line text-background-50 text-lg"></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 text-base mb-2">{v.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-14">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
              发展历程
            </h2>
            <div className="relative pl-8 border-l-2 border-background-200">
              {aboutInfo.milestones.map((m, i) => (
                <div key={i} className={`relative mb-6 last:mb-0 ${i === aboutInfo.milestones.length - 1 ? '' : ''}`}>
                  <div className="absolute -left-[2.15rem] top-0.5 w-4 h-4 rounded-full bg-accent-500 border-2 border-background-50"></div>
                  <div>
                    <span className="inline-block text-xs font-bold text-accent-600 bg-accent-100 px-2.5 py-0.5 rounded-full mb-1">
                      {m.year}
                    </span>
                    <p className="text-sm text-foreground-600 leading-relaxed mt-1">{m.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-14">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {aboutInfo.testimonials.map((t, i) => (
                <div
                  key={i}
                  className="bg-background-50 rounded-xl border border-background-200/70 p-5 md:p-6"
                >
                  <div className="flex items-center gap-1 text-accent-500 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    "{t.content}"
                  </p>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
              联系我们
            </h2>
            <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                    <i className="ri-mail-line text-primary-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500 mb-1">电子邮箱</p>
                    <p className="text-sm font-medium text-foreground-800">{aboutInfo.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
                    <i className="ri-wechat-line text-accent-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500 mb-1">微信公众号</p>
                    <p className="text-sm font-medium text-foreground-800">{aboutInfo.contact.wechat}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center shrink-0">
                    <i className="ri-weibo-line text-secondary-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500 mb-1">微博</p>
                    <p className="text-sm font-medium text-foreground-800">{aboutInfo.contact.weibo}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                    <i className="ri-phone-line text-primary-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500 mb-1">客服电话</p>
                    <p className="text-sm font-medium text-foreground-800">{aboutInfo.contact.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
                    <i className="ri-map-pin-line text-accent-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500 mb-1">办公地址</p>
                    <p className="text-sm font-medium text-foreground-800">{aboutInfo.contact.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center shrink-0">
                    <i className="ri-time-line text-secondary-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500 mb-1">工作时间</p>
                    <p className="text-sm font-medium text-foreground-800">{aboutInfo.contact.workingHours}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}